import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  CircularProgress,
  DialogActions,
  TextField,
  Typography,
} from '@material-ui/core';
import CheckIcon from 'assets/icons/CheckIcon';
import ErrorIcon from 'assets/icons/ErrorIcon';
import { DateMaskedInput } from 'components/MaskedInputs';
import { Modal } from 'components/Modal';
import { schemaModalAddCoop } from 'pages/Escola/Cadastro/etapas/ProgramasEducacionais/schema';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal } from 'store/modules/EscolasActions';
import { RootState } from 'store/reducer';
import languageValues from 'utils/LanguageUtils';
import UseProgramas from '../../../../../../hooks/useCooperativa';
import { DocumentCoop } from './DocumentCoop';
import {
  DocumentItem,
  FormDataCoop,
  ModalAddCoopProps,
} from './DocumentCoop/__types';
import './index.scss';
import { PatternFormat } from 'react-number-format';

export const ModalAddCoop: React.FC<ModalAddCoopProps> = ({ coop }) => {
  const dispatch = useDispatch();
  const isOpen = useSelector((state: RootState) => state.escolas.isOpen);
  const savingCoops = useSelector(
    (state: RootState) => state.escolas.savingCoops
  );
  const [allDocumentsUploaded, setAllDocumentsUploaded] = useState(false);
  const [documentItems, setDocumentItems] = useState<DocumentItem[]>([]);
  const [removedItems, setRemovedItems] = useState<number[]>([]);
  const [openAviso, setOpenAviso] = useState<boolean>(false);
  const { handleCoopSave, handleCoopUpdate, handleFilesCreation } =
    UseProgramas();

  const { register, handleSubmit, formState: { errors, isValid }, setValue, watch } = useForm<FormDataCoop>({
    resolver: zodResolver(schemaModalAddCoop),
    mode: 'onChange',
  });

  const closeModalAdditionCooperative = () => {
    dispatch(closeModal());
  };

  const onSubmit: SubmitHandler<FormDataCoop> = async (data) => {
    const formData = new FormData();
    const filesList = handleFilesCreation(documentItems, removedItems);

    filesList.files.forEach((f) => {
      formData.append('file', f);
    });
    filesList.fileTypes.forEach((ft) => {
      formData.append('tipoDocumento', ft.toString());
    });
    filesList.fileRemoved.forEach((ri) => {
      formData.append('tipoDocumentoExcluir', ri.toString());
    });

    formData.append('nomeCoop', data.nomeCooperativaEscolar);
    formData.append('dataFundacao', data.dataFundacao);

    if (coop) {
      handleCoopUpdate(coop.id, formData);
    } else {
      handleCoopSave(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className='form-add-coop'>
        <div className='form-add-coop__field-container'>
          <TextField
            {...register('nomeCooperativaEscolar')}
            className='form-add-coop__input'
            label='Nome da cooperativa escolar*'
            type='text'
            helperText={
              errors.nomeCooperativaEscolar
                ? errors.nomeCooperativaEscolar.message
                : ''
            }
            defaultValue={coop?.nome}
            disabled={savingCoops}
            error={!!errors.nomeCooperativaEscolar}
          />
        </div>

        <div className='form-add-coop__field-container'>
          <TextField
            {...register('dataFundacao')}
            className='form-add-coop__input'
            label='Data da fundação*'
            type='text'
            InputProps={{
              inputComponent: DateMaskedInput,
            }}
            helperText={errors.dataFundacao ? errors.dataFundacao.message : ''}
            defaultValue={coop?.dataFundacao}
            disabled={savingCoops}
            error={!!errors.dataFundacao}
          />
          <PatternFormat
            style={{ display: 'none' }}
            {...register('dataFundacao')}
            id='dataFundacao_automacao_id'
            patternChar="%"
            type='text'
            format="%%/%%/%%%%"
            value={coop?.dataFundacao}
            defaultValue={coop?.dataFundacao}
            onChange={(e) => {
              const valor = e.target.value;
              setValue('dataFundacao', valor);
            }}
          />
        </div>

        <div className='form-add-coop__field-container'>
          <div className='form-add-coop__label-container'>
            <Typography className='form-add-coop__label-documents'>
              {languageValues.pages.escolas.labels.documentos}
            </Typography>
          </div>
          <DocumentCoop
            documents={coop?.documentacoes}
            onDocumentsChange={setAllDocumentsUploaded}
            onFilesChange={setDocumentItems}
            onFilesRemove={setRemovedItems}
          />
        </div>

        <DialogActions className='form-add-coop__dialog-actions'>
          <Button
            className='form-add-coop__btn-cancel'
            disabled={savingCoops}
            startIcon={<ErrorIcon style={{ height: '16px', width: '16px' }} />}
            variant='contained'
            onClick={() => {
              setOpenAviso(true);
            }}
          >
            {languageValues.pages.escolas.buttons.cancelar}
          </Button>
          <Button
            className='form-add-coop__btn-save'
            startIcon={
              savingCoops ? (
                <CircularProgress
                  className='circular-progress'
                  size={16}
                  style={{ color: 'rgb(63, 161, 16)' }}
                />
              ) : (
                <CheckIcon
                  className='icon-check'
                  style={{ height: '16px', width: '16px' }}
                  disabled={!isValid || !allDocumentsUploaded}
                />
              )
            }
            variant='contained'
            type='submit'
            disabled={!isValid || !allDocumentsUploaded || !watch('dataFundacao') || savingCoops}
            >
            {languageValues.pages.escolas.buttons.salvar}
          </Button>
          <Button
            className='form-add-coop__btn-save-automacao'
            disabled={savingCoops}
            id='form-add-coop__btn-save-automacao-id'
            style={{ display: 'none'}}
            type='submit'
            variant='contained'
          >
            {languageValues.pages.escolas.buttons.salvar}
          </Button>
        </DialogActions>
      </form>

      <Modal
        open={openAviso}
        close={() => {
          setOpenAviso(false);
        }}
        handleBack={() => {
          setOpenAviso(false);
          closeModalAdditionCooperative();
        }}
        title={languageValues.pages.escolas.modals.cancelAddCoopModal.title}
        content={languageValues.pages.escolas.modals.cancelAddCoopModal.text}
        cancelContent={languageValues.pages.escolas.buttons.nao}
        confirmContent={languageValues.pages.escolas.buttons.sim}
      />
    </div>
  );
};
